
<template>
  <b-modal
    id="product-record-modal"
    ref="productRecordModal"
    title="Add product"
    size="lg"
  >
    <b-row>
      <b-col cols="6">
        <!-- Product Image -->
        <b-form-group label="Product Image">
          <ProductImageSelect
            :value="productImage ? [productImage] : []"
            @updated-image="handleUpdatedImage"
          />

          <div
            v-if="errors.productImage"
            class="small text-danger font-italic"
          >
            {{ errors.productImage }}
          </div>
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <!-- Product Name -->
        <b-form-group label="Product Name">
          <b-form-input
            v-model="productName"
            class="form-control"
          />

          <div
            v-if="errors.productName"
            class="small text-danger font-italic"
          >
            {{ errors.productName }}
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col cols="6">
        <!-- GTIN -->
        <b-form-group label="GTIN">
          <b-form-input
            v-model="gtin"
            class="form-control"
            type="text"
          />

          <div
            v-if="errors.gtin"
            class="small text-danger font-italic"
          >
            {{ errors.gtin }}
          </div>
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <!-- UPC -->
        <b-form-group label="UPC">
          <b-form-input
            v-model="upc"
            class="form-control"
            type="text"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col cols="6">
        <!-- Brand -->
        <b-form-group label="Brand">
          <b-form-input
            v-model="brand"
            class="form-control"
            type="text"
          />
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <!-- Variant -->
        <b-form-group label="Variant">
          <b-form-input
            v-model="variant"
            class="form-control"
            type="text"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col cols="6">
        <!-- Primary Color -->
        <b-form-group label="Primary Color">
          <b-form-input
            v-model="primaryColor"
            class="form-control"
            type="color"
          />
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <!-- Secondary Color -->
        <b-form-group label="Secondary Color">
          <b-form-input
            v-model="secondaryColor"
            class="form-control"
            type="color"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <template #modal-footer>
      <div class="d-flex justify-content-end">
        <b-button
          variant="secondary"
          @click="handleCancel"
        >
          Cancel
        </b-button>

        <b-button
          variant="primary"
          class="ml-2"
          @click="handleSave"
        >
          {{ mode === 'create' ? 'Add product' : 'Update product' }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import toastr from 'toastr';
import { mapActions } from 'vuex';
import ProductImageSelect from '@frontend/components/modules/product-list/components/ProductImageSelect.vue';

export default {
  name: 'ProductRecordModal',
  components: {
    ProductImageSelect,
  },
  emits: ['save', 'cancel'],
  data() {
    return {
      productRecordId: null,
      productImage: null,
      productName: null,
      gtin: null,
      upc: null,
      brand: null,
      variant: null,
      primaryColor: null,
      secondaryColor: null,
      errors: {},
      mode: 'create',
    };
  },
  watch: {
    productImage: {
      handler(newValue) {
        this.errors.productImage = null;
      },
    },
    productName: {
      handler(newValue) {
        this.errors.productName = null;
      },
    },
    gtin: {
      handler(newValue) {
        this.errors.gtin = null;
      },
    },
  },
  methods: {
    ...mapActions('productImageSelector', [
      'initView',
    ]),
    setData(productRecord) {
      this.productRecordId = productRecord.id || null;
      this.productImage = productRecord.product_image || null;
      this.productName = productRecord.product_name || null;
      this.gtin = productRecord.gtin || null;
      this.upc = productRecord.upc || null;
      this.brand = productRecord.brand || null;
      this.variant = productRecord.variant || null;
      this.primaryColor = productRecord.primary_color || null;
      this.secondaryColor = productRecord.secondary_color || null;
    },
    show(productRecord, mode = 'create') {
      this.mode = mode;
      this.setData(productRecord);
      this.initView({currentProductImages: this.productImage ? [this.productImage] : []});

      this.$refs.productRecordModal.show();
    },
    handleCancel() {
      this.setData({});
      this.$refs.productRecordModal.hide();
    },
    validate() {
      this.errors = {};

      if (! this.productImage) {
        this.errors.productImage = 'Product image is required';
      }

      if (! this.productName) {
        this.errors.productName = 'Product name is required';
      }

      if (! this.gtin) {
        this.errors.gtin = 'GTIN is required';
      }

      const isValid = Object.keys(this.errors).length === 0;

      if (! isValid) {
        toastr.error('Validation errors occurred');
      }

      return isValid;
    },
    handleSave() {
      if (! this.validate()) {
        return;
      }

      const productData = this.getProductData();

      this.$emit('save', { productData, mode: this.mode });
      this.$refs.productRecordModal.hide();
    },
    getProductData() {
      return {
        id: this.productRecordId,
        product_image_id: this.productImage.id,
        product_image: this.productImage,
        product_name: this.productName,
        gtin: this.gtin,
        upc: this.upc,
        brand: this.brand,
        variant: this.variant,
        primary_color: this.primaryColor,
        secondary_color: this.secondaryColor,
      };
    },
    handleUpdatedImage(productImage) {
      this.productImage = productImage;
    },
  },
}
</script>
